import { isAxiosError } from 'axios'
import { isAbortError } from '@/utils/get-user-error-message'

/** Waits before each repeat: two repeats cover a dropped connection or a server restart. */
const DEFAULT_DELAYS_MS: readonly number[] = [1000, 3000]

/**
 * An API request (axios) with no answer at all, or one the server failed. A refusal (4xx) is never worth repeating:
 * the same request gets it again. Nor is a request the caller cancelled — it also arrives with no answer, but the
 * caller no longer wants it.
 */
const isTransientApiFailure = (error: unknown): boolean => isAxiosError(error) && !isAbortError(error) && (!error.response || error.response.status >= 500)

interface RetryOptions {
    /** Which failures are worth repeating; by default an API request that got no answer or a server failure. */
    isTransient?: (error: unknown) => boolean
    /** The wait before each repeat, in turn; the last failure is passed on when they run out. */
    delaysMs?: readonly number[]
    /**
     * The wait before repeat `attempt` after `error`, or undefined to stop and pass the failure on. Replaces `delaysMs` for a caller
     * whose wait depends on the answer (a `Retry-After`) or that must stop by a deadline.
     */
    delayFor?: (error: unknown, attempt: number) => number | undefined
    /** Aborting it ends a wait at once and passes the last failure on: the caller no longer wants an answer. */
    signal?: AbortSignal
}

/** Waits `ms`, or less when `signal` aborts first; never rejects. */
const wait = (ms: number, signal?: AbortSignal): Promise<void> =>
    new Promise((resolve) => {
        const timer = setTimeout(done, ms)
        function done() {
            clearTimeout(timer)
            signal?.removeEventListener('abort', done)
            resolve()
        }
        signal?.addEventListener('abort', done, { once: true })
    })

/**
 * Send a request again when it failed in a way the same request may not fail again, waiting between tries (`delaysMs` in turn, or
 * what `delayFor` says), and pass on the last failure when the waits run out or `signal` aborts. `isTransient` says which failures
 * are worth repeating; by default an API request that got no answer or a server failure.
 *
 * ONLY for a request the server answers the same however many times it arrives — a read, or a write the server
 * makes idempotent (the Box Truck direct-upload confirmation, a document read under one request key). A lost answer does not mean
 * the request was not applied, so repeating an ordinary create or payment can do it twice.
 */
export const retryTransientRequest = async <T>(
    send: () => Promise<T>,
    { isTransient = isTransientApiFailure, delaysMs = DEFAULT_DELAYS_MS, delayFor, signal }: RetryOptions = {}
): Promise<T> => {
    for (let attempt = 0; ; attempt++) {
        try {
            return await send()
        } catch (error) {
            const delay = delayFor ? delayFor(error, attempt) : delaysMs[attempt]
            if (delay === undefined || !isTransient(error) || signal?.aborted) throw error
            await wait(delay, signal)
            if (signal?.aborted) throw error
        }
    }
}
